import { useCurrentUser } from '../../auth/CurrentUserProvider';
import { useImpersonation } from '../../auth/use-impersonation';
import { useToast } from '../../lib/use-toast';
import { useResetPassword } from './use-reset-password';
import { useUserRecord } from './use-user-record';
import { deactivateUser, reactivateUser } from './users.api';

/**
 * A user's own page: the record plus the account actions that used to sit on every list row.
 * Deactivate/reactivate have no form, so their failures toast (CLAUDE.md → Web conventions).
 */
export function useUserDetail(id: string) {
  const { state, reload } = useUserRecord(id);
  const resetPasswordState = useResetPassword();
  const impersonation = useImpersonation();
  const { state: currentUser } = useCurrentUser();
  const toast = useToast();

  async function changeStatus(action: () => Promise<void>, successMessage: string, errorMessage: string): Promise<void> {
    try {
      await action();
      await reload();
      toast.success(successMessage);
    } catch {
      toast.error(errorMessage);
    }
  }

  const signedIn = currentUser.status === 'authenticated' ? currentUser.user : undefined;

  return {
    state,
    currentUserId: signedIn?.id,
    hasTotp: signedIn?.totpEnabled ?? false,
    canImpersonate: signedIn?.permissions.includes('user.impersonate') ?? false,
    actAs: impersonation.start,
    deactivate: (name: string) => changeStatus(() => deactivateUser(id), `${name} deactivated`, `Couldn't deactivate ${name}`),
    reactivate: (name: string) => changeStatus(() => reactivateUser(id), `${name} reactivated`, `Couldn't reactivate ${name}`),
    ...resetPasswordState,
  };
}
